import { filterRecommendationProducts } from 'lib/utils/recommendation-eligibility'
import PropTypes from 'prop-types'
import React, { useState, useEffect } from 'react'
import { withI18next } from 'lib/i18n/withI18next'
import Typo from 'constants/typography'
import ResponsiveImage from 'components/images/responsive-image'
import { connect } from 'react-redux'
import compose from 'recompose/compose'
import useRecommendation from 'components/shared/useEffect/useRecommendation'
import RelatedProductLayer from 'components/shared/product/product-related/related-product-layer'
import { PDP_WISHLIST_REF_TYPE } from 'components/pdp/const'
import { hasLocizeTranslation } from 'lib/utils/locize'
import QuickAddToBagModal from 'components/shared/quick-add-to-bag-modal/QuickAddToBagModal'
import Button from 'components/common/button'
import { BUTTON_TYPES } from 'constants/button'
import WishlistCTA from 'components/pdp/interactions/wishlist-cta'
import clsx from 'clsx'
import usePersonalizedRecommendation from 'components/shared/useEffect/usePersonalizedRecommendation'
import Icon, { ICONS, ICON_SIZE } from 'components/common/icon'
import { segmentVirtualTryOnShare } from 'components/pdp/product-virtual-try-on/pdp-virtual-try-on-segment/pdpVirtualTryOnSegment'
import { segmentTrackProductAdded } from 'lib/segment'
import styles from './virtual-try-on-generated-image.scss'
import VirtualPlanOutfitModal from './virtual-plan-outfit-modal/VirtualPlanOutfitModal'

const VirtualTryOnGeneratedImage = ({
  t,
  auth,
  isPhone,
  product,
  imageUrl,
  productId,
  loadingInfo,
  closeButton,
  isShowLoadingScreen,
}) => {
  const [imgRatio, setImgRatio] = useState(1.33)
  const [recommendedProducts] = useRecommendation(productId)
  const [personalizedProduct] = usePersonalizedRecommendation(
    auth,
    productId,
    false,
    6,
    0,
  )
  const planOutfitProducts = [
    ...(recommendedProducts || []),
    ...(filterRecommendationProducts(personalizedProduct?.products) || []),
  ]
  const productCategory = product?.category
  const productUrl = window.location.href.replace('?virtual-try-on=true', '')

  const socialSharingText = `${hasLocizeTranslation(
    t,
    'SOCIAL_SHARE_TEXT',
    'Checkout my virtual try-on photoshoot generated by Pomelo',
  )} : [${imageUrl}]
  Pomelo: [${productUrl}]
  `

  useEffect(() => {
    const calculateRatio = () => {
      const width = 375
      const height = window.innerHeight * 0.8
      setImgRatio(height / width)
    }

    calculateRatio()

    // handle window resize for dynamic adjustment
    window.addEventListener('resize', calculateRatio)
    return () => window.removeEventListener('resize', calculateRatio)
  }, [])

  const handleClickSocialShare = () => {
    segmentVirtualTryOnShare({
      id_product: productId,
      site_category_name: productCategory?.link_rewrite,
      id_site_category: productCategory?.id_category,
    })
  }

  const handleCustomSegementTracking = (payload) => {
    segmentTrackProductAdded(
      { ...product },
      { size: payload?.size },
      { refData: 'virtual_try_on' },
    )
  }

  if (isPhone) {
    return (
      <React.Fragment>
        <style jsx>{styles}</style>
        {closeButton && isPhone && closeButton}
        <div className="virtual-try-on-generated-image">
          <div className="virtual-try-on-generated-image__video-wrapper">
            <ResponsiveImage
              key={imageUrl}
              className={clsx('virtual-try-on-generated-image__video', {
                'virtual-try-on-generated-image__video__loading':
                  isShowLoadingScreen,
              })}
              src={imageUrl}
              alt="product-image"
              imgRatio={imgRatio}
              isLocalImage
            />
            {isShowLoadingScreen && (
              <div
                className={clsx(
                  'virtual-try-on-generated-image__video-wrapper__loading',
                )}
              >
                {loadingInfo}
              </div>
            )}
          </div>
          <div className="virtual-try-on-generated-image__info">
            <span style={{ color: 'gray' }} className={Typo.caption}>
              {hasLocizeTranslation(
                t,
                'AI_ADJUST',
                'AI may adjust photos of you & products. Results can vary.',
              )}
            </span>
            <div className="virtual-try-on-generated-image__info__buttons">
              <VirtualPlanOutfitModal
                triggerButton={
                  <Button
                    type={BUTTON_TYPES.outlinedLight}
                    className="virtual-try-on-generated-image__info__buttons__button"
                  >
                    <Icon
                      alt="open share link"
                      src={ICONS.shareLink}
                      size={ICON_SIZE.medium}
                    />
                  </Button>
                }
              >
                <div
                  className={clsx(
                    'virtual-try-on-generated-image__social-sharing',
                    {
                      'virtual-try-on-generated-image__social-sharing__disabled-link':
                        isShowLoadingScreen,
                    },
                  )}
                >
                  <span className={Typo.subtitle1}>
                    {hasLocizeTranslation(
                      t,
                      'SHARE_THIS_LOOK',
                      'Share this look',
                    )}
                  </span>
                  <div className="virtual-try-on-generated-image__social-sharing__sharing-buttons">
                    <a
                      href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                        imageUrl,
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="virtual-try-on-generated-image__social-sharing__sharing-link"
                      onClick={() => {
                        handleClickSocialShare()
                      }}
                    >
                      <Icon
                        alt="facebook"
                        src={ICONS.facebookShare}
                        size={ICON_SIZE.extraLarge}
                      />
                      <span className={Typo.caption}>Facebook</span>
                    </a>
                    <a
                      href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
                        socialSharingText,
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="virtual-try-on-generated-image__social-sharing__sharing-link"
                      onClick={() => {
                        handleClickSocialShare()
                      }}
                    >
                      <Icon
                        alt="twitter"
                        src={ICONS.twitterShare}
                        size={ICON_SIZE.extraLarge}
                      />
                      <span className={Typo.caption}>X</span>
                    </a>
                    <a
                      href={`https://api.whatsapp.com/send?text=${encodeURIComponent(
                        `${socialSharingText}`,
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="virtual-try-on-generated-image__social-sharing__sharing-link"
                      onClick={() => {
                        handleClickSocialShare()
                      }}
                    >
                      <Icon
                        alt="whatsapp"
                        src={ICONS.whatsappShare}
                        size={ICON_SIZE.extraLarge}
                      />
                      <span className={Typo.caption}>WhatsApp</span>
                    </a>
                  </div>
                </div>
              </VirtualPlanOutfitModal>
              <VirtualPlanOutfitModal
                triggerButton={
                  <Button
                    type={BUTTON_TYPES.outlinedLight}
                    className="virtual-try-on-generated-image__info__buttons__button"
                  >
                    {hasLocizeTranslation(t, 'PLAN_OUTFIT', 'PLAN OUTFIT')}
                  </Button>
                }
              >
                <div className="virtual-try-on-generated-image__info__recommended">
                  <RelatedProductLayer
                    showTwoPerRow
                    customSkeletonProps={{ number: 2 }}
                    productId={productId}
                    products={planOutfitProducts}
                    title={hasLocizeTranslation(
                      t,
                      'PLAN_OUTFIT',
                      'PLAN OUTFIT',
                    )}
                    refType={PDP_WISHLIST_REF_TYPE.virtualTryOn}
                    elementID="Shop_the_look"
                    displayHoverInteractions
                    displayQuickAddToBag
                  />
                </div>
              </VirtualPlanOutfitModal>
              <QuickAddToBagModal
                triggerButton={
                  <Button
                    type={BUTTON_TYPES.primary}
                    className="virtual-try-on-generated-image__info__buttons__button"
                  >
                    {hasLocizeTranslation(t, 'ADD_TO_BAG', 'ADD TO BAG')}
                  </Button>
                }
                id_product={productId}
                sizes={product?.attributes}
                product_name={product?.name}
                id_site_category={product?.category?.id_category}
                customSegmentTracking={handleCustomSegementTracking}
              />
              <WishlistCTA
                product={product}
                setIsSnackBarOpen={() => {}}
                isOnWishlist={product.is_on_wishlist}
                isDeleteWishlistProductTimeOut={false}
                refType={PDP_WISHLIST_REF_TYPE.virtualTryOn}
              />
            </div>
          </div>
        </div>
      </React.Fragment>
    )
  }

  return (
    <React.Fragment>
      <style jsx>{styles}</style>
      <div className="virtual-try-on-generated-image">
        <div className="virtual-try-on-generated-image__video-wrapper">
          <ResponsiveImage
            key={imageUrl}
            className={clsx('virtual-try-on-generated-image__video', {
              'virtual-try-on-generated-image__video__loading':
                isShowLoadingScreen,
            })}
            src={imageUrl}
            alt="product-image"
            imgRatio={imgRatio}
            isLocalImage
          />
          {isShowLoadingScreen && (
            <div
              className={clsx(
                'virtual-try-on-generated-image__video-wrapper__loading',
              )}
            >
              {loadingInfo}
            </div>
          )}
        </div>
        <div className="virtual-try-on-generated-image__info">
          <div className="virtual-try-on-generated-image__info__title">
            <span className={Typo.title2}>
              {hasLocizeTranslation(t, 'TRY_IT_ON', 'Try It On')}
            </span>
            {closeButton && closeButton}
          </div>
          <span style={{ color: 'gray' }} className={Typo.caption}>
            {hasLocizeTranslation(
              t,
              'AI_ADJUST',
              'AI may adjust photos of you & products. Results can vary.',
            )}
          </span>
          <div className="virtual-try-on-generated-image__info__buttons">
            <QuickAddToBagModal
              triggerButton={
                <Button
                  type={BUTTON_TYPES.primary}
                  className="virtual-try-on-generated-image__info__buttons__button"
                >
                  {hasLocizeTranslation(t, 'ADD_TO_BAG', 'ADD TO BAG')}
                </Button>
              }
              id_product={productId}
              sizes={product?.attributes}
              product_name={product?.name}
              id_site_category={product?.category?.id_category}
              customSegmentTracking={handleCustomSegementTracking}
            />
            <WishlistCTA
              product={product}
              setIsSnackBarOpen={() => {}}
              isOnWishlist={product.is_on_wishlist}
              isDeleteWishlistProductTimeOut={false}
              refType={PDP_WISHLIST_REF_TYPE.virtualTryOn}
            />
          </div>
          <div
            className={clsx('virtual-try-on-generated-image__social-sharing', {
              'virtual-try-on-generated-image__social-sharing__disabled-link':
                isShowLoadingScreen,
            })}
          >
            <span className={Typo.subtitle1}>
              {hasLocizeTranslation(t, 'SHARE_THIS_LOOK', 'Share this look')}
            </span>
            <div className="virtual-try-on-generated-image__social-sharing__sharing-buttons">
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                  imageUrl,
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="virtual-try-on-generated-image__social-sharing__sharing-link"
                onClick={() => {
                  handleClickSocialShare()
                }}
              >
                <Icon
                  alt="facebook"
                  src={ICONS.facebookShare}
                  size={ICON_SIZE.extraLarge}
                />
                <span className={Typo.caption}>Facebook</span>
              </a>
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
                  socialSharingText,
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="virtual-try-on-generated-image__social-sharing__sharing-link"
                onClick={() => {
                  handleClickSocialShare()
                }}
              >
                <Icon
                  alt="twitter"
                  src={ICONS.twitterShare}
                  size={ICON_SIZE.extraLarge}
                />
                <span className={Typo.caption}>X</span>
              </a>
              <a
                href={`https://api.whatsapp.com/send?text=${encodeURIComponent(
                  `${socialSharingText}`,
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="virtual-try-on-generated-image__social-sharing__sharing-link"
                onClick={() => {
                  handleClickSocialShare()
                }}
              >
                <Icon
                  alt="whatsapp"
                  src={ICONS.whatsappShare}
                  size={ICON_SIZE.extraLarge}
                />
                <span className={Typo.caption}>WhatsApp</span>
              </a>
            </div>
          </div>
          <span className={Typo.subtitle1}>
            {hasLocizeTranslation(t, 'PLAN_OUTFIT', 'PLAN OUTFIT')}
          </span>
          <div className="virtual-try-on-generated-image__info__recommended">
            <RelatedProductLayer
              showTwoPerRow
              customSkeletonProps={{ number: 2 }}
              productId={productId}
              products={planOutfitProducts}
              refType={PDP_WISHLIST_REF_TYPE.virtualTryOn}
              elementID="Shop_the_look"
              displayHoverInteractions
              displayQuickAddToBag
            />
          </div>
        </div>
      </div>
    </React.Fragment>
  )
}

VirtualTryOnGeneratedImage.defaultProps = {
  isPhone: false,
  isShowLoadingScreen: false,
  product: undefined,
  products: undefined,
  loadingInfo: null,
}

VirtualTryOnGeneratedImage.propTypes = {
  isPhone: PropTypes.bool,
  isShowLoadingScreen: PropTypes.bool,
  t: PropTypes.func.isRequired,
  product: PropTypes.shape(),
  auth: PropTypes.shape({}).isRequired,
  products: PropTypes.shape({ id_recommendation: PropTypes.string }),
  imageUrl: PropTypes.string.isRequired,
  closeButton: PropTypes.node.isRequired,
  loadingInfo: PropTypes.node,
  productId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
}

export default compose(
  connect((state) => ({
    auth: state.auth,
    isPhone: state.device.isPhone,
    product: state.product.payload,
    products: state.products.payload,
    internationalization: state.internationalization,
  })),
  withI18next(),
)(VirtualTryOnGeneratedImage)
